import _ from "underscore";

import * as DataGrid from "metabase/lib/data_grid";
import { formatValue } from "metabase/lib/formatting/value";
import {
  getTableCellClickedObject,
  getTableClickedObjectRowData,
} from "metabase/visualizations/lib/table";
import { getDefaultNumberSeparators } from "metabase/visualizations/shared/settings/column";
import Table from "metabase/visualizations/visualizations/Table/Table";
import { getColumnKey } from "metabase-lib/v1/queries/utils/column-key";

const AUTO_PERCENT_COLUMN_NAME_REGEX = /%|percent/i;
const NUMERIC_BASE_TYPE_REGEX = /Float|Double|Integer|Decimal|Number/i;
export const FORMAT_PERCENT_RAW_SETTING = "jerry/format_percent_when_raw";

const hasNumericBaseType = (baseType) =>
  typeof baseType === "string" && NUMERIC_BASE_TYPE_REGEX.test(baseType);

export const getAutoPercentColumnSettings = (
  columns,
  initialColumnSettings = {},
) => {
  const nextColumnSettings = { ...initialColumnSettings };
  let didChange = false;

  for (const column of columns ?? []) {
    if (
      typeof column?.name !== "string" ||
      !AUTO_PERCENT_COLUMN_NAME_REGEX.test(column.name) ||
      !hasNumericBaseType(column.base_type)
    ) {
      continue;
    }

    const columnKey = getColumnKey(column);
    if (columnKey in nextColumnSettings) {
      continue;
    }

    nextColumnSettings[columnKey] = {
      decimals: 2,
      number_style: "percent",
    };
    didChange = true;
  }

  return didChange ? nextColumnSettings : initialColumnSettings;
};

export const getFormattedExportVisualizationSettings = (
  columns,
  visualizationSettings = {},
) => {
  const columnSettings = {
    ...getAutoPercentColumnSettings(
      columns,
      visualizationSettings?.column_settings,
    ),
  };

  for (const column of columns ?? []) {
    const columnKey = getColumnKey(column);
    const numberSeparators = getDefaultNumberSeparators(column);
    const currentColumnSettings = columnSettings[columnKey] ?? {};

    if (
      numberSeparators !== ".," &&
      currentColumnSettings.number_separators === undefined
    ) {
      columnSettings[columnKey] = {
        ...currentColumnSettings,
        number_separators: numberSeparators,
      };
    }
  }

  return {
    ...visualizationSettings,
    column_settings: columnSettings,
  };
};

export const getRawExportVisualizationSettings = (
  columns,
  visualizationSettings = {},
) => ({
  ...getFormattedExportVisualizationSettings(columns, visualizationSettings),
  [FORMAT_PERCENT_RAW_SETTING]: true,
  "table.columns": null,
  "table.column_widths": null,
  "table.pivot": false,
  "table.column_formatting": [],
});

function getVisibleData(data, settings, series) {
  if (Table.isPivoted(series, settings)) {
    const pivotIndex = _.findIndex(
      data.cols,
      (col) => col.name === settings["table.pivot_column"],
    );
    const cellIndex = _.findIndex(
      data.cols,
      (col) => col.name === settings["table.cell_column"],
    );
    const normalIndex = _.findIndex(
      data.cols,
      (col, index) => index !== pivotIndex && index !== cellIndex,
    );

    return DataGrid.pivot(data, normalIndex, pivotIndex, cellIndex, settings);
  }

  const { cols, rows } = data;
  const columnSettings = settings?.["table.columns"];
  if (!Array.isArray(columnSettings) || columnSettings.length === 0) {
    return { cols, rows };
  }

  const columnIndexes = columnSettings
    .filter((columnSetting) => columnSetting.enabled)
    .map((columnSetting) =>
      cols.findIndex((col) => col.name === columnSetting.name),
    )
    .filter((columnIndex) => columnIndex >= 0 && columnIndex < cols.length);

  return {
    cols: columnIndexes.map((i) => cols[i]),
    rows: rows.map((row) => columnIndexes.map((i) => row[i])),
  };
}

function getCellClickedObject(rowIndex, columnIndex, data, settings, series) {
  try {
    const clickedRowData = getTableClickedObjectRowData(
      series,
      rowIndex,
      columnIndex,
      settings?.["table.pivot"],
      data,
    );

    return getTableCellClickedObject(
      data,
      settings,
      rowIndex,
      columnIndex,
      settings?.["table.pivot"],
      clickedRowData,
    );
  } catch (error) {
    console.error(error);
    return undefined;
  }
}

function getCellFormattedValue(value, column, columnSettings, clicked) {
  try {
    const formattedValue = formatValue(value, {
      column,
      ...columnSettings,
      type: "cell",
      clicked,
      jsx: false,
      rich: false,
    });

    return String(formattedValue ?? "");
  } catch (error) {
    console.error(error);
    return String(value ?? "");
  }
}

function getColumnSettings(column, visualizationSettings = {}) {
  if (typeof visualizationSettings?.column === "function") {
    return visualizationSettings.column(column) ?? {};
  }

  const columnSettings = visualizationSettings?.column_settings ?? {};
  return columnSettings[getColumnKey(column)] ?? {};
}

function shouldFormatRawValue(column, columnSettings = {}) {
  return (
    columnSettings.number_style === "percent" ||
    /Percentage/i.test(String(column?.semantic_type ?? "")) ||
    /Percentage/i.test(String(column?.effective_type ?? "")) ||
    /Percentage/i.test(String(column?.base_type ?? "")) ||
    AUTO_PERCENT_COLUMN_NAME_REGEX.test(
      String(column?.display_name ?? column?.name ?? ""),
    )
  );
}

function formatRows(rows, cols, visualizationSettings, rawSeries, exportMode) {
  return rows.map((row, rowIndex) =>
    row.map((value, columnIndex) => {
      const column = cols[columnIndex];
      const columnSettings = getColumnSettings(column, visualizationSettings);

      if (exportMode === "raw") {
        return shouldFormatRawValue(column, columnSettings)
          ? getCellFormattedValue(value, column, columnSettings)
          : String(value ?? "");
      }

      const clicked = getCellClickedObject(
        rowIndex,
        columnIndex,
        { cols, rows },
        visualizationSettings,
        rawSeries,
      );

      return getCellFormattedValue(value, column, columnSettings, clicked);
    }),
  );
}

export const getExportData = ({
  exportMode,
  result,
  visualizationSettings,
  rawSeries,
}) => {
  const sourceData = result?.data;
  if (!sourceData) {
    return {
      cols: [],
      rows: [],
    };
  }

  const data =
    exportMode === "raw"
      ? sourceData
      : getVisibleData(sourceData, visualizationSettings, rawSeries);

  return {
    cols: data.cols,
    rows: formatRows(
      data.rows,
      data.cols,
      visualizationSettings,
      rawSeries,
      exportMode,
    ),
  };
};

export const getCurrentViewExportData = ({
  isShowingRawTable,
  result,
  visualizationSettings,
  rawSeries,
}) =>
  getExportData({
    exportMode: isShowingRawTable ? "raw" : "formatted",
    result,
    visualizationSettings,
    rawSeries,
  });

export const getSpreadsheetCompatibleExportData = ({
  isShowingRawTable,
  result,
  visualizationSettings,
  rawSeries,
}) => {
  const exportData = getCurrentViewExportData({
    isShowingRawTable,
    result,
    visualizationSettings,
    rawSeries,
  });

  return {
    cols: exportData.cols.map((column) => ({
      ...column,
      base_type: "type/Text",
      effective_type: "type/Text",
      semantic_type: null,
    })),
    rows: exportData.rows.map((row) => row.map((value) => String(value ?? ""))),
  };
};

export const getCurrentViewExportText = ({
  isShowingRawTable,
  result,
  visualizationSettings,
  rawSeries,
}) => {
  const exportData = getCurrentViewExportData({
    isShowingRawTable,
    result,
    visualizationSettings,
    rawSeries,
  });
  const headers = exportData.cols.map((col) => col.display_name).join("\t");
  const rows = exportData.rows.map((row) => row.join("\t")).join("\n");

  return `${headers}\n${rows}`;
};
